import React from 'react';
import { ErrorBoundary } from '@browsing/components';
import { FunctionColor, Tag, TitlePreset } from '@lux/title';

import { GridPageLayout } from '@lux/grid';
import { TextLink } from '@lux/text-link';
import type { EcommerceCreativeName, ItemSchema } from '@pegasus/tracker';
import { Campaign } from '@shared/types/mylounge';
import { trackSelectContent } from '@browsing/tracking';
import { MYLOUNGE_ROUTE } from '@client/config/constants';
import { SquareLayout } from '@client/components/mylounge/campaigns/grid/layout';
import { useResponsiveColumns } from '@client/components/mylounge/campaigns/grid/hooks';
import * as S from './index.styles';

interface CustomCampaignsListProps {
    campaigns: Campaign[];
    title: React.ReactNode;
    breadcrumbLabel: React.ReactNode;
    viewTestId?: string;
    idPrefix: string;
    trackCreativeName: EcommerceCreativeName;
    trackItemListName: ItemSchema['item_list_name'];
}

export const CustomCampaignsList: React.FC<CustomCampaignsListProps> = ({
    campaigns,
    title,
    viewTestId = 'custom-campaigns-list-view',
    breadcrumbLabel,
    idPrefix,
    trackCreativeName,
    trackItemListName,
}) => {
    const hasCampaigns = campaigns?.length > 0;
    const columnCount = useResponsiveColumns();

    const handleBackClick = () => {
        trackSelectContent('navigation', 'click', { contentId: 'back' });
    };

    return (
        <S.MainWrapper id="main" tabIndex={-1}>
            <ErrorBoundary componentName="CustomCampaignsList">
                <GridPageLayout>
                    <S.BreadcrumbWrapper>
                        <S.StyledChevronLeft aria-hidden="true" />
                        <TextLink
                            link
                            href={MYLOUNGE_ROUTE}
                            color={FunctionColor.function100}
                            onClick={handleBackClick}
                        >
                            {breadcrumbLabel}
                        </TextLink>
                    </S.BreadcrumbWrapper>
                    <S.TitleWrapper preset={TitlePreset.S} presetSm={TitlePreset.M} tag={Tag.H1}>
                        {title}
                    </S.TitleWrapper>
                    <S.CampaignsWrapper data-testid={viewTestId}>
                        {hasCampaigns && (
                            <SquareLayout
                                campaigns={campaigns}
                                columnCount={columnCount}
                                isPlusEnabled={false}
                                idPrefix={idPrefix}
                                trackCreativeName={trackCreativeName}
                                trackItemListName={trackItemListName}
                            />
                        )}
                    </S.CampaignsWrapper>
                </GridPageLayout>
            </ErrorBoundary>
        </S.MainWrapper>
    );
};
